import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FeatureAccessProvider } from "./context/FeatureAccessContext";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import FloatingGrammarly from "./components/FloatingGrammarly";
import LiveChat from "./components/LiveChat";
import MobileBackNav from "./components/MobileBackNav";
import FeatureGate from "./components/FeatureGate";
import DevToolsProtection from "./components/DevToolsProtection";

// Core pages (eagerly loaded)
import Auth from "./pages/Auth";
import StudentDashboard from "./pages/StudentDashboard";
import LandingPage from "./pages/LandingPage";
import ResetPassword from "./pages/ResetPassword";

// Lazy loaded pages (performance optimization)
const TodaysLesson = lazy(() => import("./pages/TodaysLesson"));
const Feedback = lazy(() => import("./pages/Feedback"));
const MemoryCheck = lazy(() => import("./pages/MemoryCheck"));
const MistakeDrills = lazy(() => import("./pages/MistakeDrills"));
const TeacherDashboard = lazy(() => import("./pages/TeacherDashboard"));
const TeacherReview = lazy(() => import("./pages/TeacherReview"));
const StageLearning = lazy(() => import("./pages/StageLearning"));
const Class1Learning = lazy(() => import("./pages/Class1Learning"));
const LearningHub = lazy(() => import("./pages/LearningHub"));
const ExercisePage = lazy(() => import("./pages/ExercisePage"));
const SentenceBuilderPage = lazy(() => import("./pages/SentenceBuilderPage"));
const SpellingBuilderPage = lazy(() => import("./pages/SpellingBuilderPage"));
const CommonMistakesPage = lazy(() => import("./pages/CommonMistakesPage"));
const Common1200Words = lazy(() => import("./pages/Common1200Words"));
const ScrambledWordsPage = lazy(() => import("./pages/ScrambledWordsPage"));
const SourceLibraryPage = lazy(() => import("./pages/SourceLibraryPage"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const ExamPage = lazy(() => import("./pages/ExamPage"));
const GrammarModulesHub = lazy(() => import("./pages/GrammarModulesHub"));
const ModuleLearning = lazy(() => import("./pages/ModuleLearning"));
const AchievementPage = lazy(() => import("./pages/AchievementPage"));

const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const LazyComponent = ({ children }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Allow moderator access to teacher routes
    if (user?.role === 'moderator' && allowedRoles.includes('teacher')) {
      return children;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Main Layout with Navbar
const MainLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="pt-3 sm:pt-4 pb-12 flex-1">
        <MobileBackNav />
        {children}
      </main>
      <FloatingGrammarly />
      <LiveChat />
      <footer className="py-3 text-center border-t border-gray-100 bg-white/80">
        <p className="text-[11px] text-gray-400">Technology Support & Developed by{' '}
          <a href="https://virtualswift.com/" target="_blank" rel="noopener noreferrer" className="text-purple-500 hover:text-purple-700 font-medium transition-colors" data-testid="footer-virtualswift-link">VirtualSwift</a>
        </p>
      </footer>
    </div>
  );
};

// Dashboard Router - routes based on role
const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (user?.role === 'teacher' || user?.role === 'moderator') {
    return <TeacherDashboard />;
  }
  return <StudentDashboard />;
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />} 
      />
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Auth />} 
      />
      <Route path="/reset-password/:token" element={<ResetPassword />} />

      {/* Protected Student & Teacher Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardRouter />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Student Routes */}
      <Route
        path="/lesson"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <FeatureGate path="/lesson"><LazyComponent><TodaysLesson /></LazyComponent></FeatureGate>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <LazyComponent><Feedback /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/memory"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <LazyComponent><MemoryCheck /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drills"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <FeatureGate path="/drills"><LazyComponent><MistakeDrills /></LazyComponent></FeatureGate>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stage-learning"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <LazyComponent><StageLearning /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/grammar-modules"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <LazyComponent><GrammarModulesHub /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/grammar-module/:moduleId"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <LazyComponent><ModuleLearning /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/achievements"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <LazyComponent><AchievementPage /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/class1-learning"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <LazyComponent><Class1Learning /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning-hub"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <FeatureGate path="/learning-hub"><LazyComponent><LearningHub /></LazyComponent></FeatureGate>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/learn/:sectionId/:subsectionId"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <LazyComponent><ExercisePage /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sentence-builder"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <FeatureGate path="/sentence-builder"><LazyComponent><SentenceBuilderPage /></LazyComponent></FeatureGate>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spelling-builder"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <FeatureGate path="/spelling-builder"><LazyComponent><SpellingBuilderPage /></LazyComponent></FeatureGate>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/common-1200-words"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <LazyComponent><Common1200Words /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/common-mistakes"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <FeatureGate path="/common-mistakes"><LazyComponent><CommonMistakesPage /></LazyComponent></FeatureGate>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scrambled-words"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <MainLayout>
              <LazyComponent><ScrambledWordsPage /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/source-library"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <MainLayout>
              <LazyComponent><SourceLibraryPage /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <MainLayout>
              <LazyComponent><AdminSettings /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exam/:sectionId"
        element={
          <ProtectedRoute allowedRoles={['student', 'teacher']}>
            <MainLayout>
              <LazyComponent><ExamPage /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Teacher Routes */}
      <Route
        path="/teacher"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <MainLayout>
              <LazyComponent><TeacherDashboard /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/review"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <MainLayout>
              <LazyComponent><TeacherReview /></LazyComponent>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <FeatureAccessProvider>
            <DevToolsProtection />
            <AppRoutes />
            <Toaster position="bottom-right" />
            {/* WhatsApp Floating Icon */}
            <a
              href="https://wa.me/8801346094974"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-float"
              className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg shadow-green-500/30 hover:scale-110 hover:shadow-xl hover:shadow-green-500/40 transition-all duration-300 group"
              aria-label="Chat on WhatsApp"
            >
              <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white group-hover:scale-105 transition-transform">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </a>
          </FeatureAccessProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
